import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { DEFAULT_THEME, themeVars, resolveTheme, brandPx } from "../lib/theme";
import PdfDesigner from "../components/PdfDesigner";
import BadgeDesigner from "../components/BadgeDesigner";
import { Smartphone, Tablet, Monitor, Upload, Trash2, HardHat, Home, FileText, UserCircle, Bell, Sun, Moon, Laptop, RotateCcw } from "lucide-react";

const COLORS = [["primary", "primary_color"], ["secondary", "secondary_color"], ["button_bg", "button_color"], ["bg", "bg_color"], ["card", "card_color"], ["text", "text_color"], ["sidebar", "nav_color"]];
const FONTS = ["IBM Plex Sans", "Outfit", "Manrope", "Source Sans 3", "Roboto Slab"];
const OPTS = { card_style: ["shadow", "flat", "outline"], button_style: ["rounded", "pill", "sharp"], nav_style: ["dark", "light", "colored"], icon_style: ["regular", "thin", "bold"] };

export default function ThemeBuilder() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [s, setS] = useState(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [device, setDevice] = useState("mobile");
  const logoRef = useRef(); const favRef = useRef(); const wmRef = useRef();

  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => { setS(r.data); setTheme({ ...DEFAULT_THEME, ...(r.data.theme || {}) }); }).catch(() => setErr(true)); }, []);

  const upload = async (file, key) => {
    const fd = new FormData(); fd.append("file", file); fd.append("entity_type", "branding");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((p) => ({ ...p, [key]: data.id }));
    } catch { toast.error(t("upload_failed")); }
  };
  const save = async () => {
    try {
      await api.put("/settings", { name: s.name, app_name: s.app_name, logo_file_id: s.logo_file_id || "", favicon_file_id: s.favicon_file_id || "",
        app_wordmark_file_id: s.app_wordmark_file_id || "", brand_size: s.brand_size || "medium", theme, primary_color: theme.primary });
      await refresh(); toast.success(t("save"));
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  if (!s) return <div className="tactical-card p-6 text-center text-sm text-[var(--text-3)]" data-testid="theme-loading">{err ? <button className="underline" onClick={() => window.location.reload()}>{t("error")} · {t("retry")}</button> : t("loading")}</div>;
  const set = (k, v) => setTheme((p) => ({ ...p, [k]: v }));
  const logo = s.logo_file_id ? fileUrl(s.logo_file_id) : s.logo_url;
  const wordmark = s.app_wordmark_file_id ? fileUrl(s.app_wordmark_file_id) : null;

  return (
    <div data-testid="theme-builder">
      <PageHeader title={t("theme")} subtitle={t("theme_sub")}
        action={<Button onClick={save} className="bg-[var(--amber)] text-white" data-testid="theme-save">{t("save")}</Button>} />
      <div className="grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] gap-4 min-w-0">
        <div className="space-y-4 min-w-0">
          <Section title={t("logo")}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-20 h-14 tactical-card flex items-center justify-center overflow-hidden shrink-0">
                {logo ? <img src={logo} alt="logo" className="max-h-12 max-w-full object-contain" data-testid="theme-logo-preview" /> : <HardHat className="text-[var(--amber)]" />}
              </div>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "logo_file_id")} data-testid="logo-input" />
              <Button variant="outline" size="sm" onClick={() => logoRef.current.click()} className="gap-1" data-testid="logo-upload-btn"><Upload size={14} /> {logo ? t("replace") : t("upload")}</Button>
              {logo && <Button variant="outline" size="sm" onClick={() => setS({ ...s, logo_file_id: "", logo_url: "" })} className="text-[var(--red)]" data-testid="logo-remove-btn"><Trash2 size={14} /></Button>}
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <span className="text-xs text-[var(--text-3)] w-20">Favicon</span>
              <input ref={favRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "favicon_file_id")} data-testid="favicon-input" />
              <Button variant="outline" size="sm" onClick={() => favRef.current.click()} className="gap-1" data-testid="favicon-upload-btn"><Upload size={14} /> {s.favicon_file_id ? t("replace") : t("upload")}</Button>
              {s.favicon_file_id && <Button variant="outline" size="sm" onClick={() => setS({ ...s, favicon_file_id: "" })} className="text-[var(--red)]"><Trash2 size={14} /></Button>}
            </div>
            <Input className="mt-3" value={s.app_name || ""} onChange={(e) => setS({ ...s, app_name: e.target.value })} placeholder={t("app_name")} data-testid="theme-appname" />
            <Input className="mt-2" value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} placeholder={t("company_name")} data-testid="theme-company" />
            <div className="mt-3 text-xs uppercase font-bold text-[var(--text-3)]">{t("wordmark")}</div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <div className="w-32 h-14 tactical-card flex items-center justify-center overflow-hidden shrink-0 px-2">
                {wordmark ? <img src={wordmark} alt="wordmark" className="max-h-10 max-w-full object-contain" data-testid="theme-wordmark-preview" /> : <span className="text-xs text-[var(--text-3)] truncate">{s.app_name || t("app_name")}</span>}
              </div>
              <input ref={wmRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "app_wordmark_file_id")} data-testid="wordmark-input" />
              <Button variant="outline" size="sm" onClick={() => wmRef.current.click()} className="gap-1" data-testid="wordmark-upload-btn"><Upload size={14} /> {wordmark ? t("replace") : t("upload")}</Button>
              {wordmark && <Button variant="outline" size="sm" onClick={() => setS({ ...s, app_wordmark_file_id: "" })} className="text-[var(--red)]" data-testid="wordmark-remove-btn"><Trash2 size={14} /></Button>}
            </div>
            <div className="text-xs text-[var(--text-3)] mt-1">{t("wordmark_hint")}</div>
            <div className="mt-3 text-xs uppercase font-bold text-[var(--text-3)]">{t("brand_size")}</div>
            <div className="flex gap-1 mt-2">
              {["small", "medium", "large"].map((o) => <button key={o} onClick={() => setS({ ...s, brand_size: o })} data-testid={`brand-size-${o}`} className={`flex-1 h-9 rounded-md border text-xs font-semibold ${(s.brand_size || "medium") === o ? "bg-[var(--amber)] text-white border-transparent" : "tactical-card"}`}>{t(`size_${o}`)}</button>)}
            </div>
          </Section>

          <Section title={t("mode")}>
            <div className="grid grid-cols-1 min-[380px]:grid-cols-3 gap-2">
              {[["light", Sun], ["dark", Moon], ["system", Laptop]].map(([m, I]) => (
                <button key={m} onClick={() => set("mode", m)} data-testid={`mode-${m}`}
                  className={`h-11 rounded-lg border flex items-center justify-center gap-1.5 text-sm font-semibold ${theme.mode === m ? "bg-[var(--amber)] text-white border-transparent" : "tactical-card"}`}><I size={15} /> {t(m)}</button>
              ))}
            </div>
          </Section>

          <Section title={t("colors")}>
            <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2">
              {COLORS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 tactical-card px-2 py-1.5 text-xs">
                  <input type="color" value={theme[k] || theme.primary} onChange={(e) => set(k, e.target.value)} className="w-7 h-7 rounded border-0 bg-transparent" data-testid={`color-${k}`} />
                  <span className="flex-1">{t(label)}</span>
                </label>
              ))}
              <label className="flex items-center gap-2 tactical-card px-2 py-1.5 text-xs">
                <input type="color" value={theme.button_text || "#FFFFFF"} onChange={(e) => set("button_text", e.target.value)} className="w-7 h-7 rounded border-0 bg-transparent" data-testid="color-button_text" />
                <span className="flex-1">{t("button_text_color")}</span>
              </label>
            </div>
          </Section>

          <Section title={t("style")}>
            <Field label={t("font")}>
              <select value={theme.font} onChange={(e) => set("font", e.target.value)} className="border rounded-md h-10 px-2 text-sm w-full bg-[var(--card)]" data-testid="theme-font">
                {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label={`${t("radius")}: ${theme.radius}px`}>
              <input type="range" min="0" max="24" value={theme.radius} onChange={(e) => set("radius", +e.target.value)} className="w-full" data-testid="theme-radius" />
            </Field>
            {Object.entries(OPTS).map(([k, opts]) => (
              <Field key={k} label={t(k)}>
                <div className="flex gap-1">
                  {opts.map((o) => <button key={o} onClick={() => set(k, o)} data-testid={`${k}-${o}`} className={`flex-1 h-9 rounded-md border text-xs font-semibold ${theme[k] === o ? "bg-[var(--amber)] text-white border-transparent" : "tactical-card"}`}>{t(o)}</button>)}
                </div>
              </Field>
            ))}
            <Button variant="outline" size="sm" onClick={() => setTheme(DEFAULT_THEME)} className="gap-1 w-full" data-testid="theme-reset"><RotateCcw size={14} /> {t("reset")}</Button>
          </Section>
        </div>

        <div className="tactical-card p-4 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="text-xs uppercase font-bold text-[var(--text-3)]">{t("preview")}</div>
            <div className="flex gap-1 flex-wrap">
              {[["mobile", Smartphone], ["tablet", Tablet], ["desktop", Monitor]].map(([d, I]) => (
                <button key={d} onClick={() => setDevice(d)} data-testid={`preview-${d}`} className={`h-9 px-3 rounded-md border text-xs font-semibold flex items-center gap-1 ${device === d ? "bg-[var(--amber)] text-white border-transparent" : "tactical-card"}`}><I size={14} /> {t(d)}</button>
              ))}
            </div>
          </div>
          <div className="flex justify-start sm:justify-center overflow-x-auto py-2 min-w-0 max-w-full">
            <Preview theme={theme} device={device} logo={logo} wordmark={wordmark} size={s.brand_size} appName={s.app_name || "KONVIA"} />
          </div>
        </div>
      </div>
      <div className="mt-4"><PdfDesigner key={s.id} settings={s} onSaved={refresh} /></div>
      <div className="mt-4"><BadgeDesigner key={`b${s.id}`} settings={s} onSaved={refresh} /></div>
    </div>
  );
}

function Preview({ theme, device, logo, wordmark, size, appName }) {
  const { t } = useI18n();
  const vars = themeVars(theme);
  const r = resolveTheme(theme);
  const px = brandPx(size);
  const w = device === "mobile" ? 320 : device === "tablet" ? 560 : 820;
  const h = device === "mobile" ? 600 : 480;
  const showSide = device !== "mobile";
  const Card = ({ children, className = "" }) => <div className={`tactical-card p-3 ${className}`} style={{ fontFamily: vars["--font-body"] }}>{children}</div>;
  const Btn = ({ children, className = "" }) => <div className={`h-9 px-4 flex items-center justify-center text-xs font-bold ${className}`} style={{ background: vars["--btn-bg"], color: vars["--btn-text"], borderRadius: vars["--btn-radius"] }}>{children}</div>;
  const Brand = () => (
    <span className="flex items-center gap-1.5 min-w-0" data-testid="preview-brand">
      {logo ? <img src={logo} alt="" className="object-contain shrink-0" style={{ height: px.logo * 0.6, maxWidth: px.logo }} /> : <HardHat size={16} style={{ color: r.nav_style === "colored" ? "#fff" : vars["--amber"] }} />}
      {wordmark ? <img src={wordmark} alt="" className="object-contain min-w-0" style={{ height: px.mark * 0.6, maxWidth: 100 }} />
        : <span className="font-display font-black truncate" style={{ color: r.nav_style === "colored" ? "#fff" : vars["--amber"] }}>{appName}</span>}
    </span>
  );
  return (
    <div data-testid="theme-preview" style={{ ...vars, width: w, height: h, background: vars["--bg"], color: vars["--text"], fontFamily: vars["--font-body"] }} className="rounded-2xl border-4 border-slate-800 overflow-hidden flex shadow-2xl shrink-0 text-left">
      {showSide && (
        <div style={{ background: vars["--sidebar"], width: 160 }} className="p-3 space-y-1 text-xs shrink-0" data-nav-style={r.nav_style}>
          <div className="mb-3"><Brand /></div>
          {[t("dashboard"), t("employees"), t("projects"), t("reports")].map((x, i) => (
            <div key={x} className="px-2 py-1.5 rounded" style={{ background: i === 0 ? (r.nav_style === "colored" ? "rgba(0,0,0,.35)" : vars["--amber"]) : "transparent", color: i === 0 || r.nav_style !== "light" ? "#fff" : vars["--text"], borderRadius: vars["--radius"] }}>{x}</div>
          ))}
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="h-11 flex items-center px-3 gap-2 border-b" style={{ background: vars["--card"], borderColor: vars["--border"] }}>
          {!showSide && <Brand />}
          <span className="ml-auto"><Bell size={14} /></span>
        </div>
        <div className="p-3 space-y-2 flex-1 overflow-hidden">
          <div className="font-display font-bold text-sm">{t("dashboard")}</div>
          <div className={`grid gap-2 ${showSide ? "grid-cols-3" : "grid-cols-2"}`}>
            {[[t("employees"), "12"], [t("projects"), "4"], [t("efficiency"), "96%"]].map(([l, v], i) => (
              <Card key={l} className={i === 0 ? "kpi-accent" : ""}><div className="text-[9px] uppercase" style={{ color: vars["--text-3"] }}>{l}</div><div className="font-mono-num font-bold text-lg">{v}</div></Card>
            ))}
          </div>
          <Card><div className="text-[10px] uppercase mb-1" style={{ color: vars["--text-3"] }}>{t("reports")}</div><div className="flex items-center justify-between text-xs"><span>Ivan Petrov</span><span className="px-2 py-0.5 rounded-full text-[9px] bg-emerald-100 text-emerald-700">{t("approved")}</span></div></Card>
          <div className="flex gap-2"><Btn className="flex-1">{t("start_work")}</Btn><Btn className="flex-1" >{t("add_work")}</Btn></div>
        </div>
        {!showSide && (
          <div className="h-12 grid grid-cols-3 border-t text-[9px]" style={{ background: vars["--card"], borderColor: vars["--border"] }}>
            {[[Home, t("home")], [FileText, t("reports")], [UserCircle, t("profile")]].map(([I, l], i) => <div key={l} className="flex flex-col items-center justify-center gap-0.5" style={{ color: i === 0 ? vars["--amber"] : vars["--text-3"] }}><I size={14} /> {l}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) { return <div className="tactical-card p-4"><div className="text-xs uppercase font-bold text-[var(--text-3)] mb-3">{title}</div>{children}</div>; }
function Field({ label, children }) { return <div className="mb-3"><label className="text-xs font-semibold text-[var(--text-3)] mb-1 block">{label}</label>{children}</div>; }
